import { useEffect, useId, useState } from 'react';
import type { MouseEvent, PointerEvent } from 'react';
import { Tooltip } from 'react-tooltip';
import { Button } from '../../../../../../core/components/atoms/Button';
import { Icon } from '../../../../../../core/components/atoms/Icon';
import { toast } from '../../../../../../core/components/atoms/Toast';
import { Typography } from '../../../../../../core/components/molecules/Typography';
import { blockScheduleHubCardClick } from '../../clickGuards';
import MoveContentCalendar from './MoveContentCalendar';
import MoveContentConfirmationModal from './MoveContentConfirmationModal';

const MOVE_CONTENT_TOOLTIP_OPEN_EVENT = 'move-content-tooltip-open';
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

export interface DisciplineMoveContentParams {
  discipline: string;
  fromDate?: string;
  oldDate?: string;
  sourceActivityIndex?: number;
  toDate: Date;
}

interface IMoveContentAction {
  discipline: string;
  dayKey?: string;
  originalDay?: string;
  activityIndex?: number;
  onMoveContentSuccess?: () => void;
  onMoveContentConfirm?: (
    params: DisciplineMoveContentParams
  ) => Promise<void> | void;
}

const MoveContentAction: React.FC<IMoveContentAction> = ({
  discipline,
  dayKey,
  originalDay,
  activityIndex,
  onMoveContentSuccess,
  onMoveContentConfirm,
}) => {
  const rawTooltipId = useId();
  const tooltipId = `discipline-activity-menu-${rawTooltipId.replace(/:/g, '')}`;

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  });

  const normalizedDiscipline = discipline.trim();
  const normalizedDayKey = dayKey?.trim() || undefined;
  const normalizedOriginalDay = originalDay?.trim() || undefined;
  const normalizedActivityIndex =
    activityIndex !== undefined &&
    Number.isInteger(activityIndex) &&
    activityIndex >= 0
      ? activityIndex
      : undefined;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleTooltipOpen = (event: Event) => {
      const customEvent = event as CustomEvent<string>;

      if (customEvent.detail !== tooltipId) {
        setIsTooltipOpen(false);
      }
    };

    window.addEventListener(
      MOVE_CONTENT_TOOLTIP_OPEN_EVENT,
      handleTooltipOpen as EventListener
    );

    return () => {
      window.removeEventListener(
        MOVE_CONTENT_TOOLTIP_OPEN_EVENT,
        handleTooltipOpen as EventListener
      );
    };
  }, [tooltipId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);

  const handleActionPointerDown = (event: PointerEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const handleActionClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const handleToggleTooltip = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsTooltipOpen((prev) => {
      const nextIsOpen = !prev;

      if (nextIsOpen && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(MOVE_CONTENT_TOOLTIP_OPEN_EVENT, {
            detail: tooltipId,
          })
        );
      }

      return nextIsOpen;
    });
  };

  const handleAdd = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!selectedDate) {
      return;
    }

    setIsTooltipOpen(false);
    setIsConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    if (isSubmitting) {
      return;
    }

    blockScheduleHubCardClick();
    setIsConfirmationOpen(false);
  };

  const handleConfirm = async () => {
    if (!selectedDate || isSubmitting) {
      return;
    }

    if (!normalizedDiscipline) {
      toast({
        title: 'Dados inválidos',
        message: 'Não foi possível identificar a disciplina para mover.',
        variant: 'error',
      });
      return;
    }

    if (typeof onMoveContentConfirm !== 'function') {
      toast({
        title: 'Ação indisponível',
        message: 'Não foi possível mover o conteúdo neste momento.',
        variant: 'error',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await onMoveContentConfirm({
        discipline: normalizedDiscipline,
        fromDate: normalizedDayKey,
        oldDate: normalizedOriginalDay,
        sourceActivityIndex: normalizedActivityIndex,
        toDate: selectedDate,
      });

      onMoveContentSuccess?.();
      blockScheduleHubCardClick();
      setIsConfirmationOpen(false);
      setSelectedDate(null);
    } catch {
      toast({
        title: 'Erro ao mover conteúdo',
        message: 'Não foi possível mover o conteúdo. Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isTooltipOpen && (
        <button
          type='button'
          aria-label='Fechar menu de mover conteúdo'
          onPointerDown={handleActionPointerDown}
          onClick={(event) => {
            event.stopPropagation();
            setIsTooltipOpen(false);
          }}
          className='bg-transparent fixed inset-0 z-[9998] cursor-default'
        />
      )}

      <button
        type='button'
        data-tooltip-id={tooltipId}
        onPointerDown={handleActionPointerDown}
        onClick={handleToggleTooltip}
        className='rounded-full relative flex h-6 w-6 cursor-pointer items-center justify-center text-mono-color-medium hover:bg-mono-color-lightest'
      >
        <Icon Icon='more_horiz' type='material' className='text-2xl' />
      </button>

      {isTooltipOpen ? (
        <Tooltip
          id={tooltipId}
          isOpen
          clickable
          place={isMobile ? 'top' : 'right'}
          opacity={1}
          className='!max-w-none !cursor-default !bg-mono-color-white !p-0'
          style={{
            width: '360px',
            zIndex: 10000,
            opacity: 1,
            cursor: 'default',
            // background: '',
            // color: '#2E3847',
            borderRadius: '12px',
            boxShadow: '0px 8px 24px rgba(0, 12, 41, 0.18)',
          }}
        >
          <div
            className='flex cursor-default flex-col gap-sm p-4'
            onPointerDown={handleActionPointerDown}
            onClick={handleActionClick}
          >
            <div className='flex items-center justify-between'>
              <Typography type='title' size='h5'>
                Mover conteúdo
              </Typography>
              <button
                type='button'
                onPointerDown={handleActionPointerDown}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsTooltipOpen(false);
                }}
                className='flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm border-hairline border-mono-color-light text-mono-color-darker'
              >
                <Icon Icon='close' type='material' className='text-sm' />
              </button>
            </div>

            <Typography size='small'>
              Escolha o dia para mover este conteúdo
            </Typography>

            <MoveContentCalendar
              value={selectedDate}
              onSelectDate={setSelectedDate}
            />

            <Button
              variant='contained'
              full
              disabled={!selectedDate}
              onClick={handleAdd}
              className='!cursor-pointer !bg-brand-color-primary-pure !text-card-color-default disabled:!cursor-not-allowed disabled:!bg-mono-color-medium disabled:!opacity-60'
            >
              Adicionar
            </Button>
          </div>
        </Tooltip>
      ) : null}

      <MoveContentConfirmationModal
        isOpen={isConfirmationOpen}
        selectedDate={selectedDate}
        isLoading={isSubmitting}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default MoveContentAction;
